'use client';

import { ISalePageCommentResponse } from '@/api/types';
import { Dropdown, DropdownProps } from '@/components/dropdown';
import { useMeQuery } from '@/entities';
import {
  useDeleteSalePageComment,
  useReportSalePageComment,
  useSalePageQuery,
} from '@/entities/salePage';
import { Icon, IconButton } from '@vuddy/components';
import { isNonNil, trackEvent } from '@vuddy/utils';
import { useState } from 'react';
import { toast } from 'react-toastify';
import * as styles from './commentMenu.css';

interface SalePageCommentMenuProps {
  comment: ISalePageCommentResponse;
  interactive?: boolean;
  salePageId: number;
  onEditMenuSelect: VoidFunction;
}

export const SalePageCommentMenu = ({
  comment,
  interactive = true,
  salePageId,
  onEditMenuSelect,
}: SalePageCommentMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const meQuery = useMeQuery();
  const isMine = comment.author.userId === meQuery.data?.id;
  const salePageQuery = useSalePageQuery(salePageId);

  const { mutate: deleteSalePageComment } = useDeleteSalePageComment();
  const { mutate: reportSalePageComment } = useReportSalePageComment();

  const handleDeleteMenu = () => {
    deleteSalePageComment(
      {
        salePageId,
        commentId: comment.id,
      },
      {
        onSuccess: () => {
          toast.info('댓글이 삭제되었습니다.');
        },
      }
    );
  };

  const handleReportMenu = () => {
    reportSalePageComment(comment.id, {
      onSuccess: (isReported: boolean) => {
        if (isReported) {
          toast.success('댓글이 신고되었습니다.');
        } else {
          toast.info('이미 신고한 댓글입니다.');
        }
      },
    });
  };

  const getAnalyticsEventProperties = () => ({
    channel_id: salePageQuery.data?.channelId,
    sale_type: salePageQuery.data?.type,
  });

  const handleMenuSelect = (menuId: string | number) => {
    if (menuId === 'edit') {
      onEditMenuSelect();
      trackEvent(
        'shop',
        'click_sale_comment_modification',
        getAnalyticsEventProperties()
      );
    } else if (menuId === 'delete') {
      handleDeleteMenu();
      trackEvent(
        'shop',
        'click_sale_comment_delete',
        getAnalyticsEventProperties()
      );
    } else if (menuId === 'report') {
      handleReportMenu();
      trackEvent(
        'shop',
        'click_sale_comment_report',
        getAnalyticsEventProperties()
      );
    }
  };

  const options: Array<DropdownProps['options'][number] | undefined> = [
    isMine
      ? { id: 'edit', iconName: 'OUTLINE_EDIT', label: '수정' }
      : undefined,
    isMine
      ? {
          id: 'delete',
          iconName: 'OUTLINE_TRASH',
          label: '삭제',
          color: 'danger',
        }
      : undefined,
    !isMine
      ? {
          id: 'report',
          iconName: 'OUTLINE_SIREN',
          label: '신고',
          color: 'danger',
        }
      : undefined,
  ];

  const iconProps = {
    name: 'OUTLINE_ELLIPSIS_VERTICAL',
    size: '20px',
    color: 'tertiary',
  } as const;

  return interactive ? (
    <Dropdown
      open={isOpen}
      placement="bottom-end"
      fallbackPlacements={['top-end']}
      onChangeOpen={setIsOpen}
      offset={{ mainAxis: 12 }}
      options={options.filter(isNonNil)}
      onMenuClick={handleMenuSelect}>
      <IconButton className={styles.button({ isOpen })} icon={iconProps} />
    </Dropdown>
  ) : (
    <Icon {...iconProps} />
  );
};
